/**
 * Assignee roster: staff who can be assigned a support ticket.
 *
 * Kept as the single roster source for the unified Support Inbox.
 *
 * Two queries, deliberately: `issues.assigned_to` references `auth.users`,
 * not `public.profiles`, so PostgREST cannot embed `profiles` directly off
 * `user_roles` either (same FK boundary useSupportInbox.ts documents for
 * `issues`). Hydrate names in a second query instead.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Database } from "@/lib/supabase-types";
import type { StaffRole } from "@/lib/roles";

type DisplayProfile = Pick<
  Database["public"]["Tables"]["profiles"]["Row"],
  "id" | "name" | "email"
>;
type ProfileBrief = DisplayProfile &
  Pick<Database["public"]["Tables"]["profiles"]["Row"], "is_support_agent">;

const ROSTER_ROLES: StaffRole[] = ["admin", "super_admin", "tech"];
/** Same fallback chain as the old adminDisplayName: name, then email, then a
 *  translated stand-in — never a raw id. */
export function adminDisplayName(
  p: DisplayProfile | null | undefined,
  t: (key: string, fallback?: string) => string
): string {
  return (
    p?.name?.trim() ||
    p?.email?.trim() ||
    t("support.detail.unknownAdmin", "Unknown admin")
  );
}

export function useAdminRoster(enabled = true) {
  const query = useQuery({
    queryKey: ["admin-roster"],
    enabled,
    queryFn: async (): Promise<ProfileBrief[]> => {
      const { data: roleRows, error: rolesErr } = await supabase
        .from("user_roles")
        .select("user_id, role")
        .in("role", ROSTER_ROLES);
      if (rolesErr) throw rolesErr;
      const ids = Array.from(
        new Set((roleRows || []).map((r) => String(r.user_id)))
      );
      if (!ids.length) return [];
      const { data: profs, error: profErr } = await supabase
        .from("profiles")
        .select("id, name, email, is_support_agent")
        .in("id", ids)
        .eq("is_support_agent", true);
      if (profErr) throw profErr;
      const list = ((profs || []) as ProfileBrief[]).slice();
      list.sort((a, b) =>
        String(a.name || a.email || "").localeCompare(
          String(b.name || b.email || "")
        )
      );
      return list;
    },
    staleTime: 60_000,
  });

  return {
    roster: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}
